import React from 'react';
import {
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { Section } from '../../../components/AdminContentWrapper';

const cellSx = { color: 'var(--wb-text)', borderColor: 'var(--wb-border)' };
const headSx = { ...cellSx, color: 'var(--wb-muted-85)', fontWeight: 600 };

const SERVICE_LABELS = {
  lambda: 'Lambda',
  apigw: 'API Gateway',
  dynamodb: 'DynamoDB',
  s3: 'S3',
  transfer: 'Salida a internet'
};

// Notas de por qué real y estimado no coinciden en cada servicio.
const SERVICE_NOTES = {
  lambda: 'La capa gratuita de AWS puede dejar el real en $0.',
  s3: 'El real incluye almacenamiento, que no se estima por request (se registra aparte).',
  transfer: 'El real incluye toda la salida de la cuenta, no solo la de API Gateway.'
};

/**
 * Real (Cost Explorer, toda la cuenta de AWS) contra estimado (uso medido x
 * precio de lista). `reconciliation` viene de GET /admin/usage y es null
 * hasta que corre la conciliación diaria (06:15 UTC).
 */
const ReconciliationSection = ({ reconciliation, usd }) => {
  if (!reconciliation) {
    return (
      <Section title="Conciliación con la factura de AWS">
        <Typography sx={{ color: 'var(--wb-muted-70)' }}>
          Todavía no hay datos de Cost Explorer para este período (la conciliación corre todos los días a las 06:15 UTC
          y AWS publica el costo con ~24 h de retraso).
        </Typography>
      </Section>
    );
  }

  const measuredActual = reconciliation.measured.reduce((sum, m) => sum + m.actual_usd, 0);
  const measuredEstimated = reconciliation.measured.reduce((sum, m) => sum + m.estimated_usd, 0);
  const unmeasuredActual = reconciliation.unmeasured.reduce((sum, u) => sum + u.actual_usd, 0);

  return (
    <Section
      title="Conciliación con la factura de AWS"
      subtitle={`Costo real de Cost Explorer (toda la cuenta de AWS) contra el estimado por tenant, solo en los días que tienen las dos cosas. Real comparable: ${usd(reconciliation.actual_total_usd)}.`}
    >
      {reconciliation.comparable_days.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Ningún día del período tiene a la vez costo real y medición por tenant todavía.
        </Alert>
      )}
      {reconciliation.unallocated_usd > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {usd(reconciliation.unallocated_usd)} de costo real corresponde a días sin medición por tenant (por ejemplo,
          antes de que existiera el registro de uso) y no se reparte entre tenants.
        </Alert>
      )}
      {reconciliation.pending_days.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Cost Explorer todavía no tiene {reconciliation.pending_days.length === 1 ? 'el día' : 'los días'}{' '}
          {reconciliation.pending_days.join(', ')}: el real de este período está incompleto.
        </Alert>
      )}

      <Typography sx={{ color: 'var(--wb-text)', fontWeight: 600, mb: 1 }}>Servicios medidos por tenant</Typography>
      <TableContainer sx={{ overflowX: 'auto', mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headSx}>Servicio</TableCell>
              <TableCell sx={headSx} align="right">Estimado</TableCell>
              <TableCell sx={headSx} align="right">Real</TableCell>
              <TableCell sx={headSx} align="right">Diferencia</TableCell>
              <TableCell sx={headSx}>Nota</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reconciliation.measured.map((m) => {
              const diff = m.actual_usd - m.estimated_usd;
              return (
                <TableRow key={m.service}>
                  <TableCell sx={cellSx}>{SERVICE_LABELS[m.service] || m.service}</TableCell>
                  <TableCell sx={cellSx} align="right">{usd(m.estimated_usd)}</TableCell>
                  <TableCell sx={cellSx} align="right">{usd(m.actual_usd)}</TableCell>
                  <TableCell sx={{ ...cellSx, color: diff > 0 ? 'var(--wb-warning)' : cellSx.color }} align="right">
                    {diff >= 0 ? '+' : '−'}{usd(Math.abs(diff))}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, color: 'var(--wb-muted-80)', fontSize: '0.8rem' }}>
                    {SERVICE_NOTES[m.service] || ''}
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow>
              <TableCell sx={{ ...cellSx, fontWeight: 600 }}>Subtotal</TableCell>
              <TableCell sx={{ ...cellSx, fontWeight: 600 }} align="right">{usd(measuredEstimated)}</TableCell>
              <TableCell sx={{ ...cellSx, fontWeight: 600 }} align="right">{usd(measuredActual)}</TableCell>
              <TableCell sx={cellSx} />
              <TableCell sx={cellSx} />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Typography sx={{ color: 'var(--wb-text)', fontWeight: 600, mb: 0.5 }}>
        Servicios no medidos por tenant ({usd(unmeasuredActual)})
      </Typography>
      <Typography sx={{ color: 'var(--wb-muted-70)', fontSize: '0.85rem', mb: 1 }}>
        Costo de plataforma (logs, Glue, cifrado, etc.). Se reparte entre los tenants en la columna «Asignado», en
        proporción a su costo estimado.
      </Typography>
      {reconciliation.unmeasured.length === 0 ? (
        <Typography sx={{ color: 'var(--wb-muted-70)' }}>Ninguno con costo en el período.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {reconciliation.unmeasured.map((u) => (
            <Box key={u.aws_service} sx={{
              backgroundColor: 'var(--wb-bg-surface)', border: '1px solid var(--wb-border)', borderRadius: 1, px: 1.5, py: 0.75
            }}>
              <Typography component="span" sx={{ color: 'var(--wb-muted-85)', fontSize: '0.8rem', mr: 1 }}>
                {u.aws_service}
              </Typography>
              <Typography component="span" sx={{ color: 'var(--wb-text)', fontSize: '0.85rem' }}>{usd(u.actual_usd)}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Section>
  );
};

export default ReconciliationSection;
